/** FO-GESR v4 template geometry (landscape letter 1008×612 pt). 3 blank pages.
 *  Text coords from `pdftotext -bbox formato1.pdf`; ruled grid lines detected from a 200-dpi render.
 *  Template is blank, so the renderer only overlays values/images (no masking, no grid/label redraw). */
export const ENTREGA_PDF_PAGE = { width: 1008, height: 612 } as const;

/** pdftotext bbox.yMin is glyph top; baseline sits ~0.786·fontSize below it. */
const BASELINE_FACTOR = 0.786;

export function entregaPdfDrawY(yMin: number, size = 7): number {
  return ENTREGA_PDF_PAGE.height - yMin - size * BASELINE_FACTOR;
}

/** v4 has 3 page types (no separate adjuntos/map pages). */
export const ENTREGA_PDF_TEMPLATE_PAGE = {
  remision: 0,
  encuentro: 1,
  totals: 2,
} as const;

/** Min drawn size for Adjuntos / Firma / Registro fotográfico images (PDF points ≈ px). */
export const ENTREGA_PDF_ENCOUNTER_IMAGE_MIN = 100;
const ADJUNTOS_STACK_GAP = 2;
const ADJUNTOS_STACK_PAD = 4;
/** Min row height when packing several participants on one encounter page. */
export const ENTREGA_PDF_ENCOUNTER_MIN_ROW_HEIGHT = ENTREGA_PDF_ENCOUNTER_IMAGE_MIN + ADJUNTOS_STACK_PAD;
/** Target row height for a single participant without stacked adjuntos. */
export const ENTREGA_PDF_ENCOUNTER_ROW_HEIGHT = ENTREGA_PDF_ENCOUNTER_MIN_ROW_HEIGHT;

/** Row height so each of `adjuntosCount` stacked Adjuntos images is ≥ IMAGE_MIN. */
export function entregaPdfAdjuntosStackRowHeight(adjuntosCount: number): number {
  const n = Math.max(0, Math.min(4, Math.floor(adjuntosCount)));
  if (n <= 0) return ENTREGA_PDF_ENCOUNTER_MIN_ROW_HEIGHT;
  return n * ENTREGA_PDF_ENCOUNTER_IMAGE_MIN + (n - 1) * ADJUNTOS_STACK_GAP + ADJUNTOS_STACK_PAD;
}

export type EntregaPdfLayoutRect = { x: number; y: number; width: number; height: number };
export type EntregaPdfTextField = { key: string; x: number; yMin: number; maxWidth: number };

/** Image box from top-down bbox: top edge yTop, given width/height, converted to pdf-lib. */
function imgBox(x: number, yTop: number, width: number, height: number): EntregaPdfLayoutRect {
  return { x, y: ENTREGA_PDF_PAGE.height - yTop - height, width, height };
}

/** (x1,y1,x2,y2) are pdftotext top-down bbox; convert to pdf-lib bottom-left rect. */
function bboxMask(x1: number, y1: number, x2: number, y2: number, pad = 2): EntregaPdfLayoutRect {
  return {
    x: x1 - pad,
    y: ENTREGA_PDF_PAGE.height - y2 - pad,
    width: x2 - x1 + pad * 2,
    height: y2 - y1 + pad * 2,
  };
}

// ----------------------------------------------------------------------------
// Page 1 — REMISIÓN DE ENTREGA
// ----------------------------------------------------------------------------
/** Data table: label column | value column. Divider + edges from grid detection. */
export const ENTREGA_PDF_REMISION_TABLE = { left: 38.34, divider: 470.7, right: 971.46 } as const;
const REMISION_VALUE_X = ENTREGA_PDF_REMISION_TABLE.divider + 5;
const REMISION_VALUE_MAX_WIDTH = ENTREGA_PDF_REMISION_TABLE.right - REMISION_VALUE_X - 4;

/** Value fields sit on the same baseline (yMin) as the pre-printed label. */
export const ENTREGA_PDF_REMISION_FIELDS: EntregaPdfTextField[] = [
  { key: 'puntoNombre', x: REMISION_VALUE_X, yMin: 198.59, maxWidth: REMISION_VALUE_MAX_WIDTH },
  { key: 'direccion', x: REMISION_VALUE_X, yMin: 216.83, maxWidth: REMISION_VALUE_MAX_WIDTH },
  { key: 'programa', x: REMISION_VALUE_X, yMin: 235.07, maxWidth: REMISION_VALUE_MAX_WIDTH },
  { key: 'itemNombre', x: REMISION_VALUE_X, yMin: 253.31, maxWidth: REMISION_VALUE_MAX_WIDTH },
  { key: 'nombreDespacha', x: REMISION_VALUE_X, yMin: 271.55, maxWidth: REMISION_VALUE_MAX_WIDTH },
  { key: 'vehiculo', x: REMISION_VALUE_X, yMin: 289.79, maxWidth: REMISION_VALUE_MAX_WIDTH },
  { key: 'fechaHora', x: REMISION_VALUE_X, yMin: 308.03, maxWidth: REMISION_VALUE_MAX_WIDTH },
  { key: 'cantidadSolicitadasPaquetes', x: REMISION_VALUE_X, yMin: 326.29, maxWidth: 120 },
  { key: 'cantidadEntregadasPaquetes', x: REMISION_VALUE_X, yMin: 344.53, maxWidth: 120 },
  { key: 'remanentePaquetes', x: REMISION_VALUE_X, yMin: 362.77, maxWidth: 120 },
  { key: 'cantidadSolicitadasBolsas', x: REMISION_VALUE_X, yMin: 381.01, maxWidth: 120 },
  { key: 'cantidadEntregadasBolsas', x: REMISION_VALUE_X, yMin: 399.25, maxWidth: 120 },
  { key: 'remanenteBolsas', x: REMISION_VALUE_X, yMin: 417.49, maxWidth: 120 },
  { key: 'observaciones', x: REMISION_VALUE_X, yMin: 436.09, maxWidth: REMISION_VALUE_MAX_WIDTH },
];

/** Signature block below data table. Underscore line + "CC" label are pre-printed; we only add
 *  the firma image, the signer name (above the line) and the cédula number (after "CC"). */
export const ENTREGA_PDF_REMISION_SIGNATURES = {
  entrega: {
    image: imgBox(188.6, 453, 170, 38),
    center: 273.6,
    nameYMin: 495,
    ccX: 284,
    ccYMin: 522.16,
  },
  recibe: {
    image: imgBox(655, 453, 170, 38),
    center: 740.16,
    nameYMin: 495,
    ccX: 753,
    ccYMin: 522.16,
  },
} as const;

// ----------------------------------------------------------------------------
// Page 2 — ENCUENTRO EDUCATIVO + participant table (legal notice pre-printed)
// ----------------------------------------------------------------------------
/** Pre-printed aviso de privacidad (pdftotext bbox on fo-gesr-entrega-template.pdf page 2). */
export const ENTREGA_PDF_ENCOUNTER_LEGAL_NOTICE_MASK = bboxMask(36, 138.37, 972.13, 439.32, 3);

/** Pre-printed LUGAR DE ENCUENTRO header block (labels yMin 36.18–109.64 + 10pt line). */
export const ENTREGA_PDF_ENCOUNTER_HEADER_MASK = bboxMask(36, 33, 972.13, 124, 3);

/** Pre-printed participant table column header row (continuation pages redraw body only). */
export const ENTREGA_PDF_ENCOUNTER_TABLE_HEADER_MASK = bboxMask(36, 440, 972.13, 524.34, 3);

export const ENTREGA_PDF_ENCOUNTER_HEADER_FIELDS: EntregaPdfTextField[] = [
  { key: 'puntoNombre', x: 440, yMin: 36.18, maxWidth: 525 },
  { key: 'comunaLabel', x: 245, yMin: 50.48, maxWidth: 720 },
  { key: 'fecha', x: 113, yMin: 65.0, maxWidth: 200 },
  { key: 'itemNombre', x: 181, yMin: 79.88, maxWidth: 780 },
  { key: 'nutricionistaLabel', x: 166, yMin: 94.76, maxWidth: 400 },
  { key: 'responsableLabel', x: 162, yMin: 109.64, maxWidth: 800 },
];

/** Participant table column left edges + right border (11 values → 10 columns).
 *  Adjuntos / Firma / Foto content area ≥ IMAGE_MIN (col width ≥ IMAGE_MIN + 4 pad). */
export const ENTREGA_PDF_TABLE_COL_EDGES = [
  36.18, 135, 215, 290, 370, 445, 549, 635, 739, 843, 971.46,
] as const;

/** Pre-printed column-header bottom. First-page body under full legal is ~84pt — too short for
 *  IMAGE_MIN rows, so participants render on continuation pages (legal stays uncropped). */
export const ENTREGA_PDF_ENCOUNTER_HEADER_BOTTOM = 524.34;
export const ENTREGA_PDF_ENCOUNTER_TEMPLATE_ROW_BOTTOM = 550.26;
/** First encounter page: data rows would start here (unused when IMAGE_MIN > available height). */
export const ENTREGA_PDF_ENCOUNTER_FIRST_BODY_TOP = 524.34;
/** Continuation pages: encuentro/legal chrome masked; redrawn column header band starts here. */
export const ENTREGA_PDF_ENCOUNTER_CONT_HEADER_TOP = 36;
/** Continuation pages: data rows start below redrawn column headers. */
export const ENTREGA_PDF_ENCOUNTER_CONT_BODY_TOP = 124;
export const ENTREGA_PDF_ENCOUNTER_BODY_BOTTOM = 608;
/** @deprecated use per-row bounds from encounterRowBounds() */
export const ENTREGA_PDF_ENCOUNTER_ROW = { top: 524.34, bottom: 600 } as const;

export const ENTREGA_PDF_ENCOUNTER_FIRMA_COL = 8;
export const ENTREGA_PDF_ENCOUNTER_FOTO_COL = 9;

// ----------------------------------------------------------------------------
// Page 3 — totales + ubicación + documento electrónico
// ----------------------------------------------------------------------------
export const ENTREGA_PDF_TOTALS_LINES = [
  { key: 'paquetesEntregados', x: 302, yMin: 36.42 },
  { key: 'capacitados', x: 304, yMin: 53.96 },
  { key: 'bolsasReutilizables', x: 379, yMin: 71.48 },
] as const;

/** Coordinates text placed right after the "UBICACIÓN:" label when no map image. */
export const ENTREGA_PDF_UBICACION = { x: 140, yMin: 96.44 } as const;
/** Square map image occupies the empty area below the header labels (matches the square render). */
export const ENTREGA_PDF_MAP_BOX: EntregaPdfLayoutRect = imgBox(63.84, 160, 400, 400);
/** "Documento Electrónico" is pre-printed; only the ID value is added after "ID:". */
export const ENTREGA_PDF_DOC_ID = { x: 92, yMin: 134.96 } as const;
